import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, FormControl, InputLabel, Select,
  MenuItem, Paper, Typography, Alert
} from '@mui/material';
import { timesheetService, projectService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const TimesheetEntry: React.FC = () => {
  const [projects, setProjects] = useState<any[]>([]);
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({
    projectId: '',
    date: new Date().toISOString().split('T')[0],
    taskDescription: '',
    hours: ''
  });
  const { user, isAdmin } = useAuth();

  useEffect(() => {
    if (user) {
      loadUserProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadUserProjects = async () => {
    if (!user) return;
    try {
      const data = await projectService.getUserProjects(user.id);
      setProjects(data);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const today = new Date().toISOString().split('T')[0];
    if (formData.date > today) {
      setMessage('Cannot log time for future dates.');
      return;
    }

    try {
      await timesheetService.createTimesheet({
        projectId: parseInt(formData.projectId),
        date: formData.date,
        taskDescription: formData.taskDescription,
        hours: parseFloat(formData.hours)
      });
      setMessage('Timesheet entry created successfully');
      setFormData({ projectId: '', date: new Date().toISOString().split('T')[0], taskDescription: '', hours: '' });
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error creating timesheet');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Log Your Time
      </Typography>

      {message && <Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Project</InputLabel>
            <Select
              value={formData.projectId}
              onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
              label="Project"
              required
            >
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Date"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: new Date().toISOString().split('T')[0]
            }}
            required
          />

          <TextField
            fullWidth
            label="Task Description"
            value={formData.taskDescription}
            onChange={(e) => setFormData({ ...formData, taskDescription: e.target.value })}
            margin="normal"
            multiline
            rows={3}
            required
          />

          <TextField
            fullWidth
            label="Hours"
            type="number"
            value={formData.hours}
            onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
            margin="normal"
            inputProps={{ min: 0, max: 24, step: 0.5 }}
            required
          />

          <Button type="submit" variant="contained" sx={{ mt: 2 }}>
            Log Time
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default TimesheetEntry;
